import React from "react";
import { useTranslation } from "react-i18next";

import { SkeletonBar } from "components/skeleton/SkeletonBar";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { IReportCategoryDefinition } from "pages/secure/shared/reports/reportCatalog.types";

import "components/scrollTabsLayout/scrollTabsLayout.scss";
import ReportsSearchBar from "./ReportsSearchBar";

interface IReportsListLoadingSkeletonProps {
  catalog: IReportCategoryDefinition[];
  searchHistoryKey: string;
  pinnedCount: number;
  onSearch: (value: string) => void;
}

const NAV_TAB_WIDTHS = ["72%", "58%", "80%", "64%", "70%", "52%", "76%", "60%", "68%", "54%", "74%"];
const PINNED_CHIP_WIDTHS = ["7.5rem", "9rem", "6.5rem", "8.25rem", "7rem", "10rem"];
const CARD_TITLE_WIDTHS = ["78%", "66%", "84%", "72%", "70%", "80%"];
// const CARD_DESCRIPTION_WIDTHS = ["92%", "85%", "88%", "90%", "86%", "94%"];

function ReportsListPinnedSkeleton(props: { chipCount: number }) {
  const { chipCount } = props;
  const { t } = useTranslation();

  return (
    <div className="reports-list-pinned" aria-hidden="true">
      <span className="reports-list-pinned__label">{t("Pinned")}</span>
      <span className="reports-list-pinned__divider" />
      <ul className="reports-list-pinned__chips">
        {Array.from({ length: chipCount }, (_, index) => (
          <li key={index}>
            <SkeletonBar
              className="reports-list-loading__chip-skeleton"
              width={PINNED_CHIP_WIDTHS[index % PINNED_CHIP_WIDTHS.length]}
              height="2rem"
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReportsListCardSkeleton(props: { index: number }) {
  const { index } = props;

  return (
    <article className="reports-list-card reports-list-loading__card reports-list-loading__card--compact">
      <SkeletonBar
        className="reports-list-loading__card-title"
        width={CARD_TITLE_WIDTHS[index % CARD_TITLE_WIDTHS.length]}
        height="1.5rem"
      />
      {/* {hasDescription ? (
        <SkeletonBar
          className="reports-list-loading__card-description"
          width={CARD_DESCRIPTION_WIDTHS[index % CARD_DESCRIPTION_WIDTHS.length]}
          height="1.25rem"
        />
      ) : null} */}
      <SkeletonBar className="reports-list-loading__card-action" width="1.625rem" height="1.625rem" />
    </article>
  );
}

function ReportsListSectionSkeleton(props: { expanded: boolean; hasSummary: boolean }) {
  const { expanded, hasSummary } = props;
  const sectionClassName = `reports-list-section reports-list-loading__section${
    hasSummary ? "" : " reports-list-section--no-summary"
  }`;

  return (
    <div className={sectionClassName}>
      <div className="reports-list-section__header">
        <SkeletonBar className="reports-list-loading__icon-skeleton" width="2.5rem" height="2.5rem" />
        <div className="reports-list-section__heading">
          <div className="reports-list-section__heading-text">
            <SkeletonBar width="38%" height="1.75rem" />
            {hasSummary ? <SkeletonBar className="reports-list-loading__summary-skeleton" width="72%" height="1.25rem" /> : null}
          </div>
          <SkeletonBar className="reports-list-loading__toggle-skeleton" width="2rem" height="2rem" />
        </div>
      </div>
      {expanded ? (
        <div className="reports-list-section__grid">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} role="presentation">
              <ReportsListCardSkeleton index={index} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function ReportsListLoadingSkeleton(props: IReportsListLoadingSkeletonProps) {
  const { catalog, searchHistoryKey, pinnedCount, onSearch } = props;
  const windowSize = useWindowSize();
  const showNav = (windowSize?.width ?? MOBILE_WIDTH + 1) > MOBILE_WIDTH;

  return (
    <div
      className="ui-scroll-tabs-layout ui-scroll-tabs-layout--size-md ui-scroll-tabs-layout--fill reports-list-loading"
      aria-busy="true"
    >
      {showNav ? (
        <div className="ui-scroll-tabs-layout-tabs-list">
          {catalog.map((category, index) => (
            <div key={category.id} className="reports-list-loading__nav-tab">
              <SkeletonBar width={NAV_TAB_WIDTHS[index % NAV_TAB_WIDTHS.length]} height="1.5rem" />
            </div>
          ))}
        </div>
      ) : null}

      <div className="ui-scroll-tabs-layout-content-list">
        <div className="ui-scroll-tabs-layout-content-header">
          <div className="reports-list__toolbar">
            <ReportsSearchBar historyKey={searchHistoryKey} onSearch={onSearch} />
            {pinnedCount > 0 ? <ReportsListPinnedSkeleton chipCount={pinnedCount} /> : null}
          </div>
        </div>

        {catalog.map(category => (
          <section key={category.id}>
            <ReportsListSectionSkeleton expanded={showNav} hasSummary={false} />
          </section>
        ))}
      </div>
    </div>
  );
}
